import { useState } from 'react'
import { CheckCircle, ChevronRight } from 'lucide-react'
import { useUpdateFirmProfile } from './use-firm-profile'
import type { ServiceLine } from '../engagements/types/engagement.types'
import type { FirmProfile } from './use-firm-profile'
import { useServiceLines } from '../service-lines/hooks/use-service-lines'

const TOTAL_STEPS = 3

interface Props {
  firm: FirmProfile
}

export function FirmOnboardingWizard({ firm }: Props) {
  const { activeServiceLines, getLabel } = useServiceLines()
  const [step, setStep] = useState(1)
  const [name, setName] = useState(firm.name)
  const [phone, setPhone] = useState(firm.phone ?? '')
  const [address, setAddress] = useState(firm.address ?? '')
  const [website, setWebsite] = useState(firm.website ?? '')
  const [serviceLines, setServiceLines] = useState<ServiceLine[]>(
    (firm.service_lines ?? []) as ServiceLine[]
  )
  const [error, setError] = useState<string | null>(null)

  const { mutateAsync: updateFirm, isPending: saving } = useUpdateFirmProfile()

  const toggleServiceLine = (sl: ServiceLine) => {
    setServiceLines(prev =>
      prev.includes(sl) ? prev.filter(s => s !== sl) : [...prev, sl]
    )
  }

  const handleNext = async () => {
    setError(null)
    if (step === 1 && !name.trim()) {
      setError('Firm name is required')
      return
    }
    if (step < TOTAL_STEPS) {
      setStep(s => s + 1)
      return
    }
    // Final step — save everything and mark onboarding complete
    try {
      await updateFirm({
        name: name.trim(),
        phone: phone.trim() || null,
        address: address.trim() || null,
        website: website.trim() || null,
        service_lines: serviceLines,
        onboarding_completed: true,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save firm profile')
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '1rem',
    }}>
      <div style={{
        background: 'var(--surface)', borderRadius: '1rem',
        width: '100%', maxWidth: 520, padding: '2rem',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <div style={{
              background: 'var(--color-primary)', borderRadius: '0.5rem',
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <CheckCircle size={18} color="white" />
            </div>
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: 500 }}>
              Step {step} of {TOTAL_STEPS}
            </span>
          </div>
          <h2 style={{ margin: 0, fontSize: '1.375rem', fontWeight: 700 }}>
            {step === 1 && 'Welcome to FirmNest'}
            {step === 2 && 'Select Your Service Lines'}
            {step === 3 && 'Review & Launch'}
          </h2>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            {step === 1 && "Let's set up your firm profile. You can update these details later in Settings."}
            {step === 2 && 'Choose the service lines your firm offers. This helps pre-configure task templates.'}
            {step === 3 && "Everything looks good. Click Launch to start using FirmNest."}
          </p>
        </div>

        {/* Progress bar */}
        <div style={{
          height: 4, borderRadius: 2, background: 'var(--border-light)',
          marginBottom: '1.75rem', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', borderRadius: 2, background: 'var(--color-primary)',
            width: `${(step / TOTAL_STEPS) * 100}%`, transition: 'width 0.3s',
          }} />
        </div>

        {/* Step content */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label className="form-label">Firm Name <span className="required">*</span></label>
              <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Acme CPA Firm" />
            </div>
            <div>
              <label className="form-label">Phone</label>
              <input className="form-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" />
            </div>
            <div>
              <label className="form-label">Address</label>
              <input className="form-input" value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main St, New York, NY 10001" />
            </div>
            <div>
              <label className="form-label">Website</label>
              <input className="form-input" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://yourfirm.com" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.625rem' }}>
            {activeServiceLines.map(({ key: sl }) => {
              const selected = serviceLines.includes(sl)
              return (
                <button
                  key={sl}
                  type="button"
                  onClick={() => toggleServiceLine(sl)}
                  style={{
                    padding: '0.625rem 0.875rem', borderRadius: '0.5rem', textAlign: 'left',
                    border: selected ? '2px solid var(--color-primary)' : '2px solid var(--border-light)',
                    background: selected ? 'var(--color-primary-subtle, rgba(99,102,241,0.08))' : 'transparent',
                    color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500,
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                >
                  {selected && <CheckCircle size={13} style={{ color: 'var(--color-primary)', marginRight: 6 }} />}
                  {getLabel(sl)}
                </button>
              )
            })}
          </div>
        )}

        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            {[
              { label: 'Firm Name', value: name },
              { label: 'Phone', value: phone || '—' },
              { label: 'Address', value: address || '—' },
              { label: 'Website', value: website || '—' },
              {
                label: 'Service Lines',
                value: serviceLines.length
                  ? serviceLines.map(sl => getLabel(sl)).join(', ')
                  : 'None selected',
              },
            ].map(row => (
              <div key={row.label} style={{
                display: 'flex', gap: '0.75rem', padding: '0.75rem',
                background: 'var(--surface-raised, var(--surface-secondary))',
                borderRadius: '0.5rem', fontSize: '0.875rem',
              }}>
                <span style={{ width: 110, flexShrink: 0, color: 'var(--text-muted)', fontWeight: 500 }}>
                  {row.label}
                </span>
                <span style={{ color: 'var(--text-primary)' }}>{row.value}</span>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="form-error" style={{ marginTop: '1rem' }}>{error}</div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.75rem' }}>
          {step > 1 && (
            <button className="btn-secondary" onClick={() => setStep(s => s - 1)} disabled={saving}>
              Back
            </button>
          )}
          <button className="btn-primary" onClick={handleNext} disabled={saving}>
            {saving ? 'Saving…' : step < TOTAL_STEPS ? (
              <><span>Next</span> <ChevronRight size={15} /></>
            ) : (
              '🚀 Launch FirmNest'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
